import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { map, mergeMap, Observable, withLatestFrom } from 'rxjs';
import { DsmApiResponseDataDto } from 'src/app/shared/models/dsm-api-call.model';
import { environment } from 'src/environments/environment';
import { ServiceVanActions, ServiceVanReducer, ServiceVanSelectors } from '.';
import { LoadServiceVanDetailsPayload } from './actions';
import { BaseActions } from '../../../shared/state/base';
import { ServiceVanMasterDataDto } from '../models/servicevan.model.dto';
import { RelatedWorkshopSto } from '../models/servicevan.model.sto';
import { ServiceVanMasterDataSto } from '../models/servicevan.model.sto';
import { ServiceVansService } from '../services/servicevans.service';

@Injectable()
export class ServiceVanEffects {
  private store = inject<Store<ServiceVanReducer.ServiceVanState>>(Store);
  private actions$ = inject(Actions);
  private serviceVanService = inject(ServiceVansService);

  globalRehydrate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BaseActions.REHYDRATE),
      map((_: BaseActions.RehydrateApplicationState) => {
        return new ServiceVanActions.RehydrateServiceVans();
      })
    )
  );

  globalDehydrate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BaseActions.DEHYDRATE),
      map((_: BaseActions.DehydrateApplicationState) => {
        return new ServiceVanActions.DehydrateServiceVans();
      })
    )
  );

  //-----------------------------------------------------------------------------------------------
  /**
   *
   */
  loadServiceVansPage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ServiceVanActions.LOAD_PAGE),
      map((action: ServiceVanActions.LoadServiceVansPage) => action?.payload),
      withLatestFrom(this.store.select(ServiceVanSelectors.getServiceVansLoadedAt)),
      mergeMap(([payload, loadedAt]) => {
        if (!!payload?.force || !loadedAt || new Date().getTime() - loadedAt.getTime() >= environment.reloadAfterSeconds.serviceVans * 1000.0) {
          return this.serviceVanService.loadServiceVanList(payload?.viewFilter, payload?.sort, payload?.offset).pipe(
            map((res: DsmApiResponseDataDto) => {
              const serviceVans = res?.data?.map(dto => new ServiceVanMasterDataSto(dto));
              const workshops: RelatedWorkshopSto[] = res?.related?.filter(related => related.aggregateType === 'Workshop');

              return new ServiceVanActions.LoadServiceVansPageSuccessful({
                viewId: payload?.viewId,
                serviceVans: serviceVans,
                serviceVansTotalCount: res?.meta?.pagination?.total,
                workshops: workshops,
                viewFilter: payload?.viewFilter,
                offset: payload?.offset,
              });
            })
          );
        } else {
          // already in store and not expired
          return new Observable<ServiceVanActions.LoadServiceVansStopped>(observer => {
            observer.next(new ServiceVanActions.LoadServiceVansStopped(payload?.viewId));
            observer.complete();
          });
        } //FI
      })
    )
  );

  //-----------------------------------------------------------------------------------------------
  /**
   *
   */
  loadServiceVanDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ServiceVanActions.LOAD_DETAILS),
      map((action: ServiceVanActions.LoadServiceVanDetails) => action?.payload),
      concatLatestFrom((payload: LoadServiceVanDetailsPayload) => [
        this.store.select(ServiceVanSelectors.getServiceVanDetailsLoadedAt(payload?.aggregateId)),
      ]),
      mergeMap(([payload, loadedAt]) => {
        if (!!payload?.forceLoad || !loadedAt || new Date().getTime() - loadedAt.getTime() >= environment.reloadAfterSeconds.serviceVans * 1000.0) {
          return this.serviceVanService.loadServiceVanDetails(payload?.aggregateId).pipe(
            map((res: [ServiceVanMasterDataDto, RelatedWorkshopSto[]]) => {
              const serviceVan: ServiceVanMasterDataSto = new ServiceVanMasterDataSto(res[0]);
              return new ServiceVanActions.LoadServiceVanDetailsSuccessful({
                request: payload,
                response: serviceVan,
                related: res[1],
              });
            })
          );
        } else {
          // already in store and not expired
          return new Observable<ServiceVanActions.LoadServiceVanDetailsStopped>(observer => {
            observer.next(new ServiceVanActions.LoadServiceVanDetailsStopped({ request: payload }));
            observer.complete();
          });
        }
      })
    )
  );
}
